// src/client/components/settings/XPathDefinitionsTab.tsx - V2.0 Multi-Platform
import React, { useState, useEffect } from 'react';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@client/components/ui/select';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import { CrudDialog } from '@client/components/shared/CrudDialog';
import { FormField } from '@client/components/shared/FormField';
import { Plus, Code, AlertCircle } from 'lucide-react';
import { ApiClient } from '@client/lib/api-client';
import type { XPathDefinition, InsertXPathDefinition, XPathMode, XPathContext } from '@shared/types';

// XPath Platform type (ohne 'unknown')
type XPathPlatform = 'linkedin' | 'xing';

interface XPathDefinitionsTabProps {
  // Optional: könnte von Parent kommen
}

// ✅ NEU: Platforms für Multi-Platform Support
const PLATFORMS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'xing', label: 'XING' }
];

const MODES = [
  { value: 'search', label: 'Search' },
  { value: 'group', label: 'Group' },
  { value: 'event', label: 'Event' },
  { value: 'single', label: 'Single Profile' },
  { value: 'network', label: 'Network' }
];

const CONTEXTS = [
  { value: 'document', label: 'Document' },
  { value: 'listElement', label: 'List Element' },
  { value: 'profileElement', label: 'Profile Element' }
];

// ✅ UPDATED: Platform hinzugefügt
const INITIAL_FORM_DATA: InsertXPathDefinition = {
  platform: 'linkedin',  // ← NEU: Default Platform
  mode: 'search',
  context: 'document',
  element: '',
  xpath: '',
  attribute: null,
  description: null,
  priority: 1,
  isActive: true,
  cleanupFunction: null,
  transformFunction: null
};

export default function XPathDefinitionsTab({}: XPathDefinitionsTabProps) {
  // State
  const [definitions, setDefinitions] = useState<XPathDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<XPathDefinition | null>(null);
  const [formData, setFormData] = useState<InsertXPathDefinition>(INITIAL_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);

  // Filter State - ✅ NEU: filterPlatform hinzugefügt
  const [filterPlatform, setFilterPlatform] = useState<string>('all');
  const [filterMode, setFilterMode] = useState<string>('all');
  const [filterContext, setFilterContext] = useState<string>('all');
  const [filterElement, setFilterElement] = useState<string>('all');

  // Load Definitions
  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = async () => {
    try {
      setIsLoading(true);
      const response = await ApiClient.get<any>('/xpath-definitions');
      
      // API gibt { success: true, data: [...] } zurück
      const data = response.data || response;
      
      // Sicherstellen dass es ein Array ist
      if (Array.isArray(data)) {
        setDefinitions(data);
      } else {
        console.error('[XPath] Invalid data format, expected array:', data);
        setDefinitions([]);
      }
    } catch (error) {
      console.error('[XPath] Failed to load:', error);
      setDefinitions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ UPDATED: Filtered Definitions mit Platform
  const filteredDefinitions = definitions.filter(def => {
    if (filterPlatform !== 'all' && def.platform !== filterPlatform) return false;
    if (filterMode !== 'all' && def.mode !== filterMode) return false;
    if (filterContext !== 'all' && def.context !== filterContext) return false;
    if (filterElement !== 'all' && def.element !== filterElement) return false;
    return true;
  });

  // Unique Elements for Filter
  const uniqueElements = Array.from(new Set(definitions.map(d => d.element))).sort();

  // Dialog Handlers
  const openCreateDialog = () => {
    setEditingDefinition(null);
    setFormData(INITIAL_FORM_DATA);
    setDialogOpen(true);
  };

  // ✅ UPDATED: Platform in Edit-Dialog laden
  const openEditDialog = (definition: XPathDefinition) => {
    setEditingDefinition(definition);
    setFormData({
      platform: definition.platform || 'linkedin',  // ← NEU
      mode: definition.mode,
      context: definition.context,
      element: definition.element,
      xpath: definition.xpath,
      attribute: definition.attribute,
      description: definition.description,
      priority: definition.priority,
      isActive: definition.isActive,
      cleanupFunction: definition.cleanupFunction,
      transformFunction: definition.transformFunction
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingDefinition(null);
    setFormData(INITIAL_FORM_DATA);
    setIsSaving(false);
  };

  // CRUD Operations
  const handleSave = async () => {
    // Validation
    if (!formData.platform || !formData.mode || !formData.context || !formData.element || !formData.xpath) {
      alert('Bitte füllen Sie alle Pflichtfelder aus (inkl. Platform).');
      return;
    }

    try {
      setIsSaving(true);

      if (editingDefinition) {
        // Update
        await ApiClient.put(`/xpath-definitions/${editingDefinition.id}`, formData);
      } else {
        // Create
        await ApiClient.post('/xpath-definitions', formData);
      }

      await loadDefinitions();
      closeDialog();
    } catch (error) {
      console.error('Failed to save XPath definition:', error);
      alert('Fehler beim Speichern der XPath Definition.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (definition: XPathDefinition) => {
    try {
      await ApiClient.delete(`/xpath-definitions/${definition.id}`);
      await loadDefinitions();
    } catch (error) {
      console.error('Failed to delete XPath definition:', error);
      alert('Fehler beim Löschen der XPath Definition.');
    }
  };

  const handleToggleActive = async (definition: XPathDefinition) => {
    try {
      await ApiClient.put(`/xpath-definitions/${definition.id}`, {
        isActive: !definition.isActive
      });
      await loadDefinitions();
    } catch (error) {
      console.error('Failed to toggle XPath definition:', error);
    }
  };

  // ✅ NEU: Platform Badge Color Helper
  const getPlatformBadgeVariant = (platform: string): "default" | "secondary" | "outline" => {
    switch (platform) {
      case 'linkedin': return 'default';
      case 'xing': return 'secondary';
      default: return 'outline';
    }
  };

  // ✅ UPDATED: DataTable Columns mit Platform
  const columns: TableColumn<XPathDefinition>[] = [
    // ✅ NEU: Platform Column
    {
      key: 'platform',
      header: 'Platform',
      render: (def) => (
        <Badge variant={getPlatformBadgeVariant(def.platform || 'linkedin')} className="text-xs">
          {def.platform || 'linkedin'}
        </Badge>
      ),
      className: 'w-24'
    },
    {
      key: 'mode',
      header: 'Mode',
      render: (def) => (
        <Badge variant="outline" className="text-xs">
          {def.mode}
        </Badge>
      ),
      className: 'w-24'
    },
    {
      key: 'context',
      header: 'Context',
      render: (def) => (
        <Badge variant="outline" className="text-xs">
          {def.context}
        </Badge>
      ),
      className: 'w-32'
    },
    {
      key: 'element',
      header: 'Element',
      render: (def) => (
        <span className="font-medium text-sm">{def.element}</span>
      ),
      className: 'w-40'
    },
    {
      key: 'xpath',
      header: 'XPath',
      render: (def) => (
        <div className="flex items-center gap-2">
          <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-xs block">
            {def.xpath}
          </code>
          {def.attribute && (
            <Badge variant="secondary" className="text-xs">
              @{def.attribute}
            </Badge>
          )}
        </div>
      )
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (def) => (
        <Badge variant="secondary" className="text-xs">
          {def.priority}
        </Badge>
      ),
      className: 'w-20 text-center'
    },
    {
      key: 'functions',
      header: 'Functions',
      render: (def) => (
        <div className="flex gap-1">
          {def.cleanupFunction && (
            <Badge variant="outline" className="text-xs" title={def.cleanupFunction}>
              cleanup
            </Badge>
          )}
          {def.transformFunction && (
            <Badge variant="outline" className="text-xs" title={def.transformFunction}>
              transform
            </Badge>
          )}
        </div>
      ),
      className: 'w-32'
    }
  ];

  // ✅ NEU: Stats pro Platform
  const linkedinCount = definitions.filter(d => (d.platform || 'linkedin') === 'linkedin').length;
  const xingCount = definitions.filter(d => d.platform === 'xing').length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">XPath Definitions</h2>
          <p className="text-sm text-muted-foreground">
            Manage XPath extraction rules for LinkedIn and XING
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Add XPath Definition
        </Button>
      </div>

      {/* Info Box */}
      <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Multi-Platform XPath Extraction</p>
          <p className="text-blue-700">
            XPath Definitions definieren, wie Daten aus LinkedIn und XING Seiten extrahiert werden. 
            Jede Definition ist einer Platform zugeordnet und kann mehrere Fallback-Strategien (Priority) haben.
          </p>
        </div>
      </div>

      {/* Filters - ✅ UPDATED: Platform Filter hinzugefügt */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Filter:</span>
        </div>
        
        {/* ✅ NEU: Platform Filter */}
        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Alle Platforms" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Platforms</SelectItem>
            {PLATFORMS.map(platform => (
              <SelectItem key={platform.value} value={platform.value}>
                {platform.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterMode} onValueChange={setFilterMode}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Alle Modi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Modi</SelectItem>
            {MODES.map(mode => (
              <SelectItem key={mode.value} value={mode.value}>
                {mode.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterContext} onValueChange={setFilterContext}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Alle Contexts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Contexts</SelectItem>
            {CONTEXTS.map(ctx => (
              <SelectItem key={ctx.value} value={ctx.value}>
                {ctx.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterElement} onValueChange={setFilterElement}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Alle Elements" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Elements</SelectItem>
            {uniqueElements.map(element => (
              <SelectItem key={element} value={element}>
                {element}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(filterPlatform !== 'all' || filterMode !== 'all' || filterContext !== 'all' || filterElement !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setFilterPlatform('all');
              setFilterMode('all');
              setFilterContext('all');
              setFilterElement('all');
            }}
          >
            Filter zurücksetzen
          </Button>
        )}
      </div>

      {/* ✅ UPDATED: Stats mit Platform-Breakdown */}
      <div className="flex gap-4 text-sm text-muted-foreground">
        <span>
          Gesamt: <strong>{definitions.length}</strong>
        </span>
        <span>
          Gefiltert: <strong>{filteredDefinitions.length}</strong>
        </span>
        <span className="text-blue-600">
          LinkedIn: <strong>{linkedinCount}</strong>
        </span>
        <span className="text-green-600">
          XING: <strong>{xingCount}</strong>
        </span>
        <span>
          Aktiv: <strong>{definitions.filter(d => d.isActive).length}</strong>
        </span>
      </div>

      {/* DataTable */}
      <DataTable
        data={filteredDefinitions}
        columns={columns}
        loading={isLoading}
        getRowKey={(def) => def.id}
        emptyState={{
          icon: <Code className="h-12 w-12 text-muted-foreground" />,
          title: 'Keine XPath Definitionen',
          description: 'Erstellen Sie XPath Definitionen für die Datenextraktion.'
        }}
        actions={{
          onEdit: (def) => openEditDialog(def),
          onDelete: (def) => handleDelete(def),
          onToggle: (def) => handleToggleActive(def)
        }}
        deleteConfirmation={{
          title: 'XPath Definition löschen?',
          description: (def) => 
            `Möchten Sie die XPath Definition für "${def.element}" (${def.platform || 'linkedin'}/${def.mode}/${def.context}) wirklich löschen?`
        }}
      />

      {/* CrudDialog - ✅ UPDATED: Platform Feld hinzugefügt */}
      <CrudDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={editingDefinition ? 'edit' : 'create'}
        title={editingDefinition ? 'XPath Definition bearbeiten' : 'XPath Definition erstellen'}
        description="Konfigurieren Sie die XPath-basierte Datenextraktion für LinkedIn oder XING"
        onSave={handleSave}
        onCancel={closeDialog}
        isSaving={isSaving}
      >
        <div className="space-y-4">
          {/* ✅ NEU: Platform, Mode & Context in einer Zeile */}
          <div className="grid grid-cols-3 gap-4">
            <FormField
              id="platform"
              label="Platform"
              type="select"
              value={formData.platform}
              onChange={(value) => setFormData({...formData, platform: value as XPathPlatform})}
              options={PLATFORMS}
              required
            />

            <FormField
              id="mode"
              label="Mode"
              type="select"
              value={formData.mode}
              onChange={(value) => setFormData({...formData, mode: value as XPathMode})}
              options={MODES}
              required
            />

            <FormField
              id="context"
              label="Context"
              type="select"
              value={formData.context}
              onChange={(value) => setFormData({...formData, context: value as XPathContext})}
              options={CONTEXTS}
              required
            />
          </div>

          {/* Element */}
          <FormField
            id="element"
            label="Element"
            value={formData.element}
            onChange={(value) => setFormData({...formData, element: value as string})}
            placeholder="z.B. fullName, profileUrl, connectionDegree"
            hint="Eindeutiger Name des zu extrahierenden Elements"
            required
          />

          {/* XPath */}
          <FormField
            id="xpath"
            label="XPath Query"
            type="textarea"
            value={formData.xpath}
            onChange={(value) => setFormData({...formData, xpath: value as string})}
            placeholder=".//button[contains(@aria-label, 'Nachricht an')]"
            rows={3}
            hint="Relativer XPath vom Context-Element aus"
            required
          />

          {/* Attribute & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              id="attribute"
              label="Attribute"
              value={formData.attribute || ''}
              onChange={(value) => setFormData({...formData, attribute: value ? value as string : null})}
              placeholder="href, src, alt"
              hint="Leer lassen für textContent"
            />

            <FormField
              id="priority"
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(value) => setFormData({...formData, priority: parseInt(value as string)})}
              hint="1 = höchste Priorität"
            />
          </div>

          {/* Cleanup Function */}
          <FormField
            id="cleanupFunction"
            label="Cleanup Function"
            type="textarea"
            value={formData.cleanupFunction || ''}
            onChange={(value) => setFormData({...formData, cleanupFunction: value ? value as string : null})}
            placeholder="value.replace('Nachricht an ', '').trim()"
            rows={2}
            hint="JavaScript Code (value als Parameter)"
          />

          {/* Transform Function */}
          <FormField
            id="transformFunction"
            label="Transform Function"
            type="textarea"
            value={formData.transformFunction || ''}
            onChange={(value) => setFormData({...formData, transformFunction: value ? value as string : null})}
            placeholder="parseInt(value)"
            rows={2}
            hint="JavaScript Code für Typ-Konvertierung"
          />

          {/* Description */}
          <FormField
            id="description"
            label="Beschreibung"
            type="textarea"
            value={formData.description || ''}
            onChange={(value) => setFormData({...formData, description: value ? value as string : null})}
            placeholder="Beschreibung der XPath-Strategie"
            rows={2}
          />

          {/* Active */}
          <FormField
            id="isActive"
            label="Aktiv"
            type="switch"
            value={formData.isActive}
            onChange={(value) => setFormData({...formData, isActive: value as boolean})}
            hint="Deaktivierte Definitionen werden nicht für die Extraktion verwendet"
          />
        </div>
      </CrudDialog>
    </div>
  );
}